// src/pages/AdminPrivacyRequests.tsx

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserRole } from '../hooks/useUserRole';
import Navigation from '../components/Navigation';
import { PrivacyRequest, REQUEST_TYPES } from '../types/privacyRequest';
import {
  getAllPrivacyRequests,
  getPrivacyRequest,
  updatePrivacyRequestStatus,
  verifyPrivacyRequest
} from '../lib/privacy/privacyRequestApi';
import { aggregateUserData } from '../lib/privacy/dataAggregation';
import { exportUserData } from '../lib/privacy/dataExport';
import { deleteUserData } from '../lib/privacy/dataDeletion';
import { checkAndSendDeadlineReminders, getRequestsApproachingDeadline } from '../lib/privacy/deadlineReminders';
import { auth } from '../firebase/config';

const AdminPrivacyRequests: React.FC = () => {
  const { user, role, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<PrivacyRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  
  // Selected request
  const [selectedRequest, setSelectedRequest] = useState<PrivacyRequest | null>(null);
  const [processing, setProcessing] = useState(false);
  const [exportFormat, setExportFormat] = useState<'json' | 'csv'>('json');
  const [internalNotes, setInternalNotes] = useState('');
  const [checkingDeadlines, setCheckingDeadlines] = useState(false);
  
  useEffect(() => {
    if (!roleLoading && role !== 'admin') {
      navigate('/admin-dashboard');
    }
  }, [roleLoading, role, navigate]);
  
  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const allRequests = await getAllPrivacyRequests();
      setRequests(allRequests);
    } catch (err: any) {
      console.error('Error loading privacy requests:', err);
      setError('Failed to load privacy requests: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    if (role === 'admin') {
      loadRequests();
    }
  }, [role, loadRequests]);
  
  const handleVerify = async (requestId: string) => {
    try {
      setProcessing(true);
      setError('');
      await verifyPrivacyRequest(requestId, 'manual');
      setSuccess('Request verified successfully.');
      await loadRequests();
    } catch (err: any) {
      console.error('Error verifying request:', err);
      setError('Failed to verify request: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };
  
  const handleExportData = async (request: PrivacyRequest) => {
    if (!request.userId || !request.email || !request.id) return;
    
    try {
      setProcessing(true);
      setError('');
      
      const userData = await aggregateUserData(request.userId, request.email, request.id);
      exportUserData(userData, exportFormat);
      
      // Update request with export URL (we'd normally upload to storage, but for now just mark as completed)
      if (user && request.id) {
        await updatePrivacyRequestStatus(
          request.id,
          'completed',
          user.uid,
          `Data exported in ${exportFormat.toUpperCase()} format`,
          'data-exported-' + Date.now() // In production, this would be a secure storage URL
        );
      }
      
      setSuccess('Data exported successfully.');
      await loadRequests();
    } catch (err: any) {
      console.error('Error exporting data:', err);
      setError('Failed to export data: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };
  
  const handleDeleteData = async (request: PrivacyRequest) => {
    if (!request.userId || !request.id || !user) return;
    
    if (!window.confirm('Are you sure you want to proceed with deletion? This action cannot be undone.')) {
      return;
    }
    
    try {
      setProcessing(true);
      setError('');
      
      const deletionResult = await deleteUserData(request.userId, false);
      
      await updatePrivacyRequestStatus(
        request.id,
        'completed',
        user.uid,
        internalNotes || 'Data deletion completed',
        undefined,
        deletionResult
      );
      
      setSuccess('Data deletion completed. Some data may have been retained for legal compliance.');
      setInternalNotes('');
      await loadRequests();
      setSelectedRequest(null);
    } catch (err: any) {
      console.error('Error deleting data:', err);
      setError('Failed to delete data: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };
  
  const handleUpdateStatus = async (requestId: string, newStatus: PrivacyRequest['status'], rejectionReason?: string) => {
    if (!user) return;
    
    try {
      setProcessing(true);
      setError('');
      
      const updateData: any = {
        status: newStatus,
        processedBy: user.uid
      };
      
      if (rejectionReason) {
        updateData.rejectionReason = rejectionReason;
      }
      
      if (internalNotes) {
        const request = await getPrivacyRequest(requestId);
        const existingNotes = request?.notes || [];
        updateData.notes = [...existingNotes, internalNotes];
      }
      
      await updatePrivacyRequestStatus(requestId, newStatus, user.uid, internalNotes);
      
      setSuccess('Request status updated successfully.');
      setInternalNotes('');
      await loadRequests();
      setSelectedRequest(null);
    } catch (err: any) {
      console.error('Error updating status:', err);
      setError('Failed to update status: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };
  
  const filteredRequests = requests.filter((request) => {
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
    const matchesType = typeFilter === 'all' || request.requestType === typeFilter;
    return matchesStatus && matchesType;
  });
  
  const getStatusColor = (status: PrivacyRequest['status']) => {
    switch (status) {
      case 'completed':
        return '#28a745';
      case 'in_progress':
        return '#ffc107';
      case 'rejected':
        return '#dc3545';
      case 'expired':
        return '#6c757d';
      default:
        return '#007bff';
    }
  };
  
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return 'N/A';
    }
  };
  
  const getDaysUntilDeadline = (deadline: any) => {
    if (!deadline) return null;
    try {
      const deadlineDate = deadline.toDate ? deadline.toDate() : new Date(deadline);
      const now = new Date();
      const diffTime = deadlineDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return null;
    }
  };
  
  const handleCheckDeadlines = async () => {
    try {
      setCheckingDeadlines(true);
      setError('');
      await checkAndSendDeadlineReminders();
      setSuccess('Deadline reminders checked and sent successfully.');
      await loadRequests();
    } catch (err: any) {
      console.error('Error checking deadlines:', err);
      setError('Failed to check deadlines: ' + err.message);
    } finally {
      setCheckingDeadlines(false);
    }
  };
  
  const approachingDeadlineRequests = getRequestsApproachingDeadline(requests);
  
  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    in_progress: requests.filter(r => r.status === 'in_progress' || r.status === 'verifying').length,
    completed: requests.filter(r => r.status === 'completed').length,
    overdue: requests.filter(r => {
      const days = getDaysUntilDeadline(r.deadline);
      return days !== null && days < 0 && r.status !== 'completed';
    }).length,
    approachingDeadline: approachingDeadlineRequests.length
  };
  
  if (roleLoading || loading) {
    return (
      <div>
        <Navigation />
        <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
      </div>
    );
  }
  
  return (
    <div>
      <Navigation />
      <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
        <h1 style={{ marginBottom: '1rem' }}>Privacy Request Management</h1>
        
        {error && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}
        
        {success && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#d4edda',
            color: '#155724',
            borderRadius: '4px',
            marginBottom: '1rem'
          }}>
            {success}
          </div>
        )}
        
        {/* Statistics Dashboard */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#007bff' }}>{stats.total}</div>
            <div style={{ color: '#666' }}>Total Requests</div>
          </div>
          <div style={{ padding: '1rem', backgroundColor: '#fff3cd', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#856404' }}>{stats.pending}</div>
            <div style={{ color: '#666' }}>Pending</div>
          </div>
          <div style={{ padding: '1rem', backgroundColor: '#ffeaa7', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f39c12' }}>{stats.in_progress}</div>
            <div style={{ color: '#666' }}>In Progress</div>
          </div>
          <div style={{ padding: '1rem', backgroundColor: '#d4edda', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#155724' }}>{stats.completed}</div>
            <div style={{ color: '#666' }}>Completed</div>
          </div>
          <div style={{ padding: '1rem', backgroundColor: stats.overdue > 0 ? '#f8d7da' : '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: stats.overdue > 0 ? '#721c24' : '#666' }}>{stats.overdue}</div>
            <div style={{ color: '#666' }}>Overdue</div>
          </div>
          <div style={{ padding: '1rem', backgroundColor: stats.approachingDeadline > 0 ? '#fff3cd' : '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: stats.approachingDeadline > 0 ? '#856404' : '#666' }}>{stats.approachingDeadline}</div>
            <div style={{ color: '#666' }}>Approaching Deadline</div>
          </div>
        </div>
        
        {/* Deadline Reminder Actions */}
        <div style={{
          marginBottom: '2rem',
          padding: '1rem',
          backgroundColor: '#e7f3ff',
          borderRadius: '8px',
          border: '1px solid #b3d7ff'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '0.5rem' }}>Deadline Reminders</h3>
          <p style={{ marginBottom: '1rem', fontSize: '0.875rem', color: '#495057' }}>
            Reminders are automatically sent at 30, 14, 7, 3, and 1 days before deadlines. 
            You can manually check and send reminders for approaching deadlines.
          </p>
          <button
            onClick={handleCheckDeadlines}
            disabled={checkingDeadlines}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: checkingDeadlines ? '#ccc' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: checkingDeadlines ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: '500'
            }}
          >
            {checkingDeadlines ? 'Checking...' : 'Check & Send Deadline Reminders'}
          </button>
          {approachingDeadlineRequests.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500' }}>Requests Approaching Deadline:</p>
              <ul style={{ margin: 0, paddingLeft: '1.5rem', fontSize: '0.875rem' }}>
                {approachingDeadlineRequests.slice(0, 5).map((req) => {
                  const days = getDaysUntilDeadline(req.deadline);
                  return (
                    <li key={req.id}>
                      {req.email} - {REQUEST_TYPES.find(t => t.id === req.requestType)?.title} 
                      ({days} days remaining)
                    </li>
                  );
                })}
                {approachingDeadlineRequests.length > 5 && (
                  <li>...and {approachingDeadlineRequests.length - 5} more</li>
                )}
              </ul>
            </div>
          )}
        </div>
        
        {/* Filters */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '2rem',
          flexWrap: 'wrap'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Filter by Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '0.5rem',
                borderRadius: '4px',
                border: '1px solid #ced4da',
                fontSize: '1rem'
              }}
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="verifying">Verifying</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Filter by Type:</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              style={{
                padding: '0.5rem',
                borderRadius: '4px',
                border: '1px solid #ced4da',
                fontSize: '1rem'
              }}
            >
              <option value="all">All Types</option>
              {REQUEST_TYPES.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.title}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Requests List */}
        <div style={{ display: 'grid', gridTemplateColumns: selectedRequest ? '1fr 400px' : '1fr', gap: '2rem' }}>
          <div>
            <h2 style={{ marginBottom: '1rem' }}>Requests ({filteredRequests.length})</h2>
            {filteredRequests.length === 0 ? (
              <p style={{ color: '#666' }}>No requests found matching the filters.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {filteredRequests.map((request) => {
                  const requestType = REQUEST_TYPES.find(t => t.id === request.requestType);
                  const daysUntilDeadline = getDaysUntilDeadline(request.deadline);
                  const isOverdue = daysUntilDeadline !== null && daysUntilDeadline < 0;
                  
                  return (
                    <div
                      key={request.id}
                      onClick={() => setSelectedRequest(request)}
                      style={{
                        padding: '1.5rem',
                        backgroundColor: selectedRequest?.id === request.id ? '#e7f3ff' : '#fff',
                        border: '2px solid',
                        borderColor: selectedRequest?.id === request.id ? '#007bff' : '#dee2e6',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                        <div>
                          <h3 style={{ margin: 0, marginBottom: '0.25rem' }}>{requestType?.title || request.requestType}</h3>
                          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>
                            User: {request.email} ({request.userId.substring(0, 8)}...)
                          </p>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <span
                            style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '4px',
                              backgroundColor: getStatusColor(request.status),
                              color: 'white',
                              fontSize: '0.75rem',
                              fontWeight: '500'
                            }}
                          >
                            {request.status.replace('_', ' ').toUpperCase()}
                          </span>
                          {request.deadline && (
                            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.75rem', color: isOverdue ? '#dc3545' : '#666', fontWeight: isOverdue ? 'bold' : 'normal' }}>
                              {isOverdue ? '⚠ OVERDUE' : `${daysUntilDeadline} days left`}
                            </p>
                          )}
                        </div>
                      </div>
                      <p style={{ margin: 0, color: '#495057', fontSize: '0.875rem' }}>
                        Submitted: {formatDate(request.submittedAt)}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* Request Detail Panel */}
          {selectedRequest && (
            <div style={{
              padding: '1.5rem',
              backgroundColor: '#fff',
              border: '1px solid #dee2e6',
              borderRadius: '8px',
              position: 'sticky',
              top: '2rem',
              maxHeight: 'calc(100vh - 4rem)',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0 }}>
                  {REQUEST_TYPES.find(t => t.id === selectedRequest.requestType)?.title}
                </h3>
                <button
                  onClick={() => setSelectedRequest(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#666'
                  }}
                >
                  ×
                </button>
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong>User ID:</strong> {selectedRequest.userId}
                </div>
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong>Email:</strong> {selectedRequest.email}
                </div>
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong>Status:</strong>{' '}
                  <span style={{ color: getStatusColor(selectedRequest.status) }}>
                    {selectedRequest.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong>Submitted:</strong> {formatDate(selectedRequest.submittedAt)}
                </div>
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong>Deadline:</strong> {formatDate(selectedRequest.deadline)}
                  {(() => {
                    const days = getDaysUntilDeadline(selectedRequest.deadline);
                    if (days !== null && days < 0 && selectedRequest.status !== 'completed') {
                      return <span style={{ color: '#dc3545', fontWeight: 'bold' }}> (OVERDUE by {Math.abs(days)} days)</span>;
                    }
                    return days !== null ? <span> ({days} days remaining)</span> : null;
                  })()}
                </div>
                {selectedRequest.description && (
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Description:</strong>
                    <p style={{ margin: '0.5rem 0 0 0', color: '#495057' }}>{selectedRequest.description}</p>
                  </div>
                )}
              </div>
              
              {/* Action Buttons */}
              <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                <h4 style={{ marginTop: 0, marginBottom: '0.5rem' }}>Actions</h4>
                
                {selectedRequest.status === 'pending' && (
                  <button
                    onClick={() => handleVerify(selectedRequest.id!)}
                    disabled={processing}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      marginBottom: '0.5rem',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: processing ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Verify Request
                  </button>
                )}
                
                {(selectedRequest.requestType === 'know' || selectedRequest.requestType === 'portability') && selectedRequest.status !== 'completed' && (
                  <>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Export Format:</label>
                      <select
                        value={exportFormat}
                        onChange={(e) => setExportFormat(e.target.value as 'json' | 'csv')}
                        style={{ width: '100%', padding: '0.25rem', borderRadius: '4px', border: '1px solid #ced4da' }}
                      >
                        <option value="json">JSON</option>
                        <option value="csv">CSV</option>
                      </select>
                    </div>
                    <button
                      onClick={() => handleExportData(selectedRequest)}
                      disabled={processing}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        marginBottom: '0.5rem',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: processing ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Export & Complete Request
                    </button>
                  </>
                )}
                
                {selectedRequest.requestType === 'delete' && selectedRequest.status !== 'completed' && (
                  <button
                    onClick={() => handleDeleteData(selectedRequest)}
                    disabled={processing}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      marginBottom: '0.5rem',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: processing ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Delete Data & Complete
                  </button>
                )}
                
                <div style={{ marginTop: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Internal Notes:</label>
                  <textarea
                    value={internalNotes}
                    onChange={(e) => setInternalNotes(e.target.value)}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      border: '1px solid #ced4da',
                      fontSize: '0.875rem',
                      fontFamily: 'inherit'
                    }}
                    placeholder="Add internal notes..."
                  />
                </div>
                
                <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handleUpdateStatus(selectedRequest.id!, 'completed')}
                    disabled={processing}
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: processing ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    Mark Complete
                  </button>
                  <button
                    onClick={() => {
                      const reason = window.prompt('Enter rejection reason:');
                      if (reason) {
                        handleUpdateStatus(selectedRequest.id!, 'rejected', reason);
                      }
                    }}
                    disabled={processing}
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: processing ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    Reject
                  </button>
                </div>
              </div>
              
              {/* Deletion Results */}
              {selectedRequest.deletionExceptions && selectedRequest.deletionExceptions.length > 0 && (
                <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#fff3cd', borderRadius: '4px' }}>
                  <strong>Deletion Notes:</strong>
                  <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem', fontSize: '0.875rem' }}>
                    {selectedRequest.deletionExceptions.map((exception, idx) => (
                      <li key={idx}>{exception}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Notes History */}
              {selectedRequest.notes && selectedRequest.notes.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Notes History:</strong>
                  <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem', fontSize: '0.875rem' }}>
                    {selectedRequest.notes.map((note, idx) => (
                      <li key={idx}>{note}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPrivacyRequests;
